import { CircularProgress, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@material-ui/core';
import { Field, FieldArray, FieldProps, Formik } from 'formik';
import React, { useEffect, useState } from 'react';
import { ITransformer, ITransformerCompany } from '../../../../@types/model/transformer/transformer';
import StadiumButton from '../../../customComponents/button/StadiumButton';
import BudgetTablePaginationField from '../field/BudgetTablePagination';
import SPUBudgetFieldArray from '../field/SPUField';
import TransformerBudgetHelper, { ITransformerBudget, ISPUBudgetFormValues, ISPUBudget } from '../../../../@types/model/transformer/budget/budget';

interface ISPUBudgetFormProps {
    startDate : number;
    hideDetail : boolean;

    transformers : Array<ITransformer>;

    initialValue ?: ITransformerBudget | null;

    onSubmit : (value : ISPUBudgetFormValues) => void;
    
    company : ITransformerCompany;
    area : string;
    transformer : string | null;
    accountNumber : string | null;
    period : number | null;
}

const SPUBudgetForm = (props : ISPUBudgetFormProps) : React.ReactElement => {
    const [initialFormValue, setInitialFormValue] = useState<ISPUBudgetFormValues | null>(null);
    const [currentPage, setCurrentPage] = useState<number>(0);
    const [rowsPerPage, setRowsPerPage] = useState<number>(50);
    const [rowsPerPageOptions] = useState<Array<number>>([10, 20, 30, 50]);

    const setInit = () : void => {
        const { startDate, company, area, transformers, initialValue } = props;

        setInitialFormValue(
            TransformerBudgetHelper.initSPUForm(
                startDate,
                transformers,
                company,
                area,
                initialValue as ITransformerBudget<ISPUBudget>,
            ), 
        );
    };

    useEffect(() => {
        setInit();
    }, [props.startDate, props.company.id, props.area]);

    useEffect(() => {
        onPageChange(null, 0);
    }, [props.transformer, props.accountNumber, props.period]);

    const onSubmit = (values : ISPUBudgetFormValues) : void => {
        if (!props.onSubmit) return;

        props.onSubmit(values);
    };

    const onPageChange = (event : React.MouseEvent<HTMLButtonElement> | null, page : number) : void => {
        setCurrentPage(page);
    };

    const onRowsPerPageChange = (event ?: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) : void => {
        if (!event) return;
        setRowsPerPage(Number(event.target.value));
    };

    return (
        <div className='fdc flx1'>
            {
                !!initialFormValue &&
                <Formik
                    initialValues={initialFormValue}
                    validationSchema={TransformerBudgetHelper.formSPUSchema()}
                    onSubmit={onSubmit}
                    validateOnBlur={false}
                    validateOnChange={false}
                    enableReinitialize
                >
                    {
                        (formik) => (
                            <>
                                {
                                    !formik.isSubmitting &&
                                    <div className='fdc flx1 budget bs oys'>
                                        <Table stickyHeader>
                                            <TableHead className='budget-table-header'>
                                                <TableRow>
                                                    <TableCell component='th' scope='row' align='left' width={80}>
                                                        Period
                                                    </TableCell>
                                                    <TableCell component='th' scope='row' align='left' width={100}>
                                                        Company
                                                    </TableCell>
                                                    <TableCell component='th' scope='row' align='left' width={150}>
                                                        Area
                                                    </TableCell>
                                                    <TableCell component='th' scope='row' align='left' width={100}>
                                                        EP Nr
                                                    </TableCell>
                                                    {
                                                        !props.hideDetail &&
                                                        <>
                                                            <TableCell component='th' scope='row' align='left' width={350}>
                                                                Sub Area
                                                            </TableCell>
                                                            <TableCell component='th' scope='row' align='left' width={200}>
                                                                Acc Nr
                                                            </TableCell>
                                                        </>
                                                    }
                                                    <TableCell className='lwb bcpd' component='th' scope='row' align='left'>
                                                        NEW BUDGETED
                                                    </TableCell>
                                                    <TableCell className='lwb bcp' component='th' scope='row' align='left'>
                                                        COMMENT
                                                    </TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody className='budget-table-body'>
                                                <FieldArray name='values'>
                                                    {
                                                        (array) => (
                                                            <SPUBudgetFieldArray
                                                                {...array}
                                                                accountNumber={props.accountNumber}
                                                                area={props.area}
                                                                company={props.company}
                                                                period={props.period}
                                                                currentPage={currentPage}
                                                                rowsPerPage={rowsPerPage}
                                                                transformer={props.transformer}
                                                                hideDetail={props.hideDetail}
                                                            />
                                                        )
                                                    }
                                                </FieldArray>
                                            </TableBody>
                                        </Table>
                                    </div>
                                }
                                {
                                    formik.isSubmitting &&
                                    <div className='fdc flx1 aic jcc'>
                                        <CircularProgress />
                                    </div>
                                }
                                <div className={'fdr aic'}>
                                    <span className='flx1'></span>
                                    <Field>
                                        {
                                            (params : FieldProps<unknown>) => (
                                                <BudgetTablePaginationField
                                                    {...params}
                                                    accountNumber={props.accountNumber}
                                                    area={props.area}
                                                    company={props.company}
                                                    period={props.period}
                                                    currentPage={currentPage}
                                                    rowsPerPage={rowsPerPage}
                                                    transformer={props.transformer}
                                                    rowsPerPageOptions={rowsPerPageOptions}
                                                    onPageChange={onPageChange}
                                                    onRowsPerPageChange={onRowsPerPageChange}
                                                />
                                            )
                                        }
                                    </Field>
                                </div>
                                <div className='fdr aife jcfe mt15'>
                                    <StadiumButton
                                        style={{
                                            height: 42,
                                            borderRadius: 40,
                                        }}
                                        className='jcc aic p0'
                                        color='primary'
                                        type='button'
                                        onClick={formik.submitForm}
                                        aria-label='Submit'
                                        disabled={formik.isSubmitting || !formik.isValid}
                                    >
                                        <Typography className='fw600 fs16 cp ml20 mr20'>SAVE</Typography>
                                    </StadiumButton>
                                </div>
                            </>
                        )
                    }
                </Formik>

            }
        </div>
    );
};

export default SPUBudgetForm;
